"use client";

import { useDroppable } from '@dnd-kit/core';
import { ContentBlock, add_blocks_metadata } from '@/features/editor/utils/content-blocks';
import { SortableContentBlock } from './SortableContentBlock';

interface ContentDropZoneProps {
  content: ContentBlock[];
  onUpdate: (id: string, block: ContentBlock) => void;
  onDelete: (id: string) => void;
}

export function ContentDropZone({ content, onUpdate, onDelete }: ContentDropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'content-area',
  });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[400px] p-6 rounded-2xl border transition-colors ${
        isOver 
          ? 'border-blue-300 bg-blue-50/50' 
          : 'border-gray-200 bg-white'
      } shadow-sm`}
    >
      {content.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <div className="text-6xl mb-4">📝</div>
          <p className="text-lg font-medium">No content blocks yet</p>
          <p className="text-sm">Drag blocks from the palette to get started</p>
          {isOver && (
            <div className="mt-4 text-blue-600 font-medium">
              Drop here to add your first block!
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {add_blocks_metadata(content).map((block, index) => (
            <div key={`block-${index}`} className="relative">
              {/* Drop zone above each block */}
              <DropIndicator 
                id={`content-${index}`}
                isFirst={index === 0}
              />
              
              <SortableContentBlock
                // Cast is safe for runtime since SortableContentBlock only reads id
                block={{...block, id: `block-${index}`} as unknown as ContentBlock}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            </div>
          ))}
          
          {/* Drop zone after last block */}
          <DropIndicator 
            id={`content-${content.length}`}
            isLast={true}
          />
        </div>
      )}
    </div>
  );
}

interface DropIndicatorProps {
  id: string;
  isFirst?: boolean;
  isLast?: boolean;
}

function DropIndicator({ id, isFirst, isLast }: DropIndicatorProps) {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`h-2 transition-all duration-200 ${
        isOver 
          ? 'bg-blue-400 rounded-full mx-4' 
          : 'bg-transparent'
      }`}
    >
      {isOver && (
        <div className="text-center text-blue-600 text-sm font-medium py-1">
          Drop here
        </div>
      )}
    </div>
  );
}
